import { Collection } from 'firestorter';

import Collections from '../../utils/collections';

import BaseDocument from '../../Model/BaseDocument';

/**
 * lead configuration option attribute names enum
 */
const LeadConfigOptions = {
  IS_EMAIL_RESPONSE_ENABLED: 'isEmailResponseEnabled',
  IS_TEXT_BACK_ENABLED: 'isTextBackEnabled',
  CONTACT_NO: 'contactNo',
  EMAIL_ADDRESS: 'emailAddress',
  IS_LEAD_SUBMISSION_EMAIL_ENABLED: 'isLeadSubmissionEmailEnabled',
  LEAD_SUBMISSION_EMAIL: 'leadSubmissionEmail',
  IS_WELCOME_EMAIL_ENABLED: 'sendWelcomeEmail',
};

/**
 * Automated Text Setter touchpoints, used as the config key under
 * `automatedTextSetterConfigs`. Each touchpoint owns its own settings, so adding a
 * later touchpoint is a new entry here plus a new key in the config document — no
 * change to how the settings are read or written.
 *
 * @enum {string}
 */
const SetterTouchPoint = {
  FIRST_MESSAGE: 'FIRST_MESSAGE',
};

/**
 * Per-touchpoint Automated Text Setter configuration attribute names enum
 */
const AutomatedTextSetterOptions = {
  IS_ENABLED: 'enabled',
  SEND_PERCENTAGE: 'sendPercentage',
  MESSAGE_TEMPLATE: 'messageTemplate',
};

/**
 * Default share of eligible leads assigned to the test group.
 */
const DEFAULT_SETTER_SEND_PERCENTAGE = 10;

/**
 * Highest selectable send percentage.
 */
const MAX_SETTER_SEND_PERCENTAGE = 100;

/**
 * RCS outreach experiment (S2-9713) attribute names, under `rcsOutreachConfig`. A share of the
 * coach's new leads get their outreach from the Avida RCS sender instead of SendBlue.
 */
const RcsOutreachOptions = {
  IS_ENABLED: 'enabled',
  RCS_PERCENTAGE: 'rcsPercentage',
};

/**
 * Share of new leads in the RCS arm before one is chosen: an even split, for a true A/B test.
 */
const DEFAULT_RCS_PERCENTAGE = 50;

const orderType = {
  ASC: 'ASC',
  DESC: 'DESC',
};

/**
 * Options of activity feed auto archival setting
 */
const AutoArchiveDays = {
  TWO_WEEKS: 14,
  ONE_MONTH: 30,
  TWO_MONTHS: 60,
  THREE_MONTHS: 90,
};

/**
 * Options of workout reminder notification setting
 */
const WorkoutReminderDays = {
  ONE_DAY: 1,
  TWO_DAY: 2,
  THREE_DAY: 3,
  ONE_WEEK: 7,
  TWO_WEEKS: 14,
};

const ClientJourneyWeeks = {
  WEEK_01: 1,
  WEEK_02: 2,
  WEEK_03: 3,
  WEEk_04: 4,
  WEEK_05: 5,
  WEEK_06: 6,
  WEEK_07: 7,
  WEEK_08: 8,
  WEEK_09: 9,
  WEEK_10: 10,
  WEEK_11: 11,
  WEEK_12: 12,
  WEEK_13: 13,
  WEEK_14: 14,
  WEEK_15: 15,
  WEEK_16: 16,
  WEEK_17: 17,
  WEEK_18: 18,
  WEEK_19: 19,
  WEEK_20: 20,
  WEEK_21: 21,
  WEEK_22: 22,
  WEEK_23: 23,
  WEEK_24: 24,
  WEEK_25: 25,
  WEEK_26: 26,
};

const MessageSendDays = {
  FIRST_DAY: 1,
  SECOND_DAY: 2,
  THIRD_DAY: 3,
  FOURTH_DAY: 4,
  FIFTH_DAY: 5,
  SIXTH_DAY: 6,
  SEVENTH_DAY: 7,
};

/**
 * class representing configuration attributes of a coach
 *
 * @class CoachConfig
 * @extends BaseDocument
 */
class CoachConfig extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.COACH_CONFIG}/${id}`, opts);
  }

  /**
   * Get coach's activity feed configurations
   * @return {object}
   * @property {object} defaultOrder  - Default order of activity feed
   */
  get activityFeedConfigs() {
    return this.data?.activityFeedConfigs;
  }

  /**
   * Get default order of each attribute of activity feed
   * @return {object}
   * @property {string} attributeName - attribute name of default order
   * @property {string} order - Value of order (ASC/DESC)
   */
  get activityFeedDefaultOrder() {
    return this.activityFeedConfigs?.defaultOrder;
  }

  /**
   * Get attribute name of default order set
   * @return {string}
   */
  get activityFeedDefaultOrderAttributeName() {
    return this.activityFeedDefaultOrder?.attributeName;
  }

  /**
   * Get default order of attribute (ASC/DESC)
   * @return {string}
   */
  get activityFeedDefaultOrderValue() {
    return this.activityFeedDefaultOrder?.order || orderType.ASC;
  }

  /**
   * Number of days coach activities will be considered to be active before automatically removing them.
   * @return {number}
   */
  get daysToAutoArchive() {
    return this.data.activityFeedConfigs?.daysToAutoArchive || AutoArchiveDays.TWO_WEEKS;
  }

  /**
   * Number of days before a client needs new workouts when the coach will be notified.
   * @return {number}
   */
  get daysToRemindWorkoutAssign() {
    return this.data.notificationConfigs?.daysToRemindWorkoutAssign || WorkoutReminderDays.ONE_DAY;
  }

  /**
   * Is workout and meal plan notification enabled for future start clients.
   * @return {boolean}
   */
  get isFutureStartNotificationEnabled() {
    return !!this.data.notificationConfigs?.isFutureStartNotificationEnabled;
  }

  /**
   * Get coach's lead configurations
   * @return {object}
   */
  get leadConfigs() {
    return this.data.leadConfigs || {};
  }

  /**
   * Is text responding enabled for leads or not
   * @return {boolean}
   */
  get isTextBackEnabled() {
    return !!this.leadConfigs?.isTextBackEnabled;
  }

  /**
   * Is email responding enabled for leads or not
   * @return {boolean}
   */
  get isEmailResponseEnabled() {
    return !!this.leadConfigs?.isEmailResponseEnabled;
  }

  /**
   * Email address of the coach configured for leads.
   * @return {string}
   */
  get coachEmailAddress() {
    return this.leadConfigs?.emailAddress || '';
  }

  /**
   * Phone number of the coach configured for leads.
   * This is only available for non IS coaches, as IS enabled coaches are handled with internal numbers.
   * @return {string}
   */
  get coachContactNumber() {
    return this.leadConfigs?.contactNo || '';
  }

  /**
   * Is new lead submission email enabled or not
   * @return {boolean}
   */
  get isLeadSubmissionEmailEnabled() {
    return !!this.leadConfigs?.isLeadSubmissionEmailEnabled;
  }

  /**
   * Email address to send new lead notifications
   * @return {string}
   */
  get leadSubmissionEmail() {
    return this.leadConfigs?.leadSubmissionEmail || '';
  }

  /**
   * Is send welcome email enabled or not
   * @return {boolean}
   */
  get isLeadWelcomeEmailEnabled() {
    return !!this.leadConfigs?.sendWelcomeEmail;
  }

  /**
   * Get the coach's Automated Text Setter configurations
   * @return {object}
   */
  get automatedTextSetterConfigs() {
    return this.data.automatedTextSetterConfigs || {};
  }

  /**
   * Settings for a single Automated Text Setter touchpoint.
   *
   * A touchpoint the coach has never been configured for resolves to an empty object, which
   * reads as disabled — a touchpoint only sends where an admin has explicitly turned it on.
   *
   * @param {string} touchPoint - A SetterTouchPoint value
   * @return {object} The touchpoint's settings
   */
  getAutomatedTextSetterConfigsByTouchPoint = (touchPoint) => this.automatedTextSetterConfigs[touchPoint] || {};

  get rcsOutreachConfig() {
    return this.data.rcsOutreachConfig || {};
  }

  /**
   * Is the RCS outreach experiment on for the coach. Never configured reads as off.
   * @return {boolean}
   */
  get isRcsOutreachEnabled() {
    return !!this.rcsOutreachConfig[RcsOutreachOptions.IS_ENABLED];
  }

  /**
   * Share of new leads sent their outreach over RCS, as a whole percentage.
   * @return {number}
   */
  get rcsOutreachPercentage() {
    const rcsPercentage = this.rcsOutreachConfig[RcsOutreachOptions.RCS_PERCENTAGE];
    return Number.isFinite(rcsPercentage) ? rcsPercentage : DEFAULT_RCS_PERCENTAGE;
  }

  /**
   * Is the coach's Instagram account held at a verification challenge.
   *
   * Set by the Instagram agent when it hits one, and it stops every automated DM for this coach —
   * whatever the touchpoint settings say — until an admin resolves the challenge and clears it.
   *
   * @return {boolean}
   */
  get isAutomatedTextSetterCircuitBroken() {
    return !!this.automatedTextSetterConfigs.runtime?.isCircuitBroken;
  }

  /**
   * Is the given Automated Text Setter touchpoint enabled for the coach or not
   * @param {string} touchPoint - A SetterTouchPoint value
   * @return {boolean}
   */
  isAutomatedTextSetterEnabled = (touchPoint) => (
    !!this.getAutomatedTextSetterConfigsByTouchPoint(touchPoint).enabled
  );

  /**
   * Share of eligible leads assigned to the test group for a touchpoint, as a whole percentage.
   * @param {string} touchPoint - A SetterTouchPoint value
   * @return {number}
   */
  getAutomatedTextSetterSendPercentage = (touchPoint) => {
    const { sendPercentage } = this.getAutomatedTextSetterConfigsByTouchPoint(touchPoint);
    return Number.isFinite(sendPercentage) ? sendPercentage : DEFAULT_SETTER_SEND_PERCENTAGE;
  };

  /**
   * Message template for a touchpoint, supporting the {lead_name} placeholder.
   * @param {string} touchPoint - A SetterTouchPoint value
   * @return {string}
   */
  getAutomatedTextSetterMessageTemplate = (touchPoint) => (
    this.getAutomatedTextSetterConfigsByTouchPoint(touchPoint).messageTemplate || ''
  );

  /**
   * Get coach's meal plan configurations
   * @return {object}
   */
  get mealPlanConfigs() {
    return this.data.mealPlanConfigs || {};
  }

  /**
   * List of public recipe IDs that should still be included for this coach,
   * even when public recipes are globally filtered out.
   * @return {Array<string>}
   */
  get alwaysIncludeRecipes() {
    const ids = this.mealPlanConfigs.alwaysIncludeRecipes;
    if (!Array.isArray(ids)) {
      return [];
    }
    return [...new Set(ids.filter((id) => typeof id === 'string' && id.trim().length > 0))];
  }

  /**
   * List of archived public recipes for the coach
   * @return {Array}
   */
  get archivedRecipes() {
    return this.mealPlanConfigs.archivedPublicRecipes || [];
  }

  /**
   * Is webhook setup for the coach or not
   * @return {boolean}
   */
  get isWebhookSetup() {
    return !!this.data.isWebhookSetup;
  }

  /**
   * Is email domain created for the coach or not
   * @return {boolean}
   */
  get isEmailDomainCreated() {
    return !!this.data.isEmailDomainCreated;
  }

  /**
   * Get the time when the warmup process is enabled for the coach email domain
   * @return {Date}
   */
  get warmupEnabledAt() {
    return this.data.warmupEnabledAt?.toDate();
  }

  /**
   * Get whether the force send is enabled for the coach's client journey weekly messaging
   * @return {boolean}
   */
  get forcedWeeklyMessages() {
    return !!this.data.forcedWeeklyMessages;
  }

  /**
   * Represents a weekly message configuration
   * @typedef {Object} WeeklyMessage
   * @property {number} week - The week number (1-12)
   * @property {boolean} isEnabled - Whether this week's message is enabled
   * @property {string} message - The content of the message
   * @property {number} messageSendDay - The day of the week to send the message (1-7)
   * @property {string} messageSendTime - The time to send the message in 24h format (HH:mm)
   * @property {Array<string>} [attachments] - Optional array of attachment URLs
   */

  /**
   * Gets the list of client journey weekly messaging for the coach
   * @type {WeeklyMessage[]}
   * @returns {WeeklyMessage[]} Array of weekly message configurations
   */
  get clientJourneyWeeklyMessaging() {
    return this.data.clientJourneyWeeklyMessaging || [];
  }

  /**
   * Check if the coach has enabled the Pod feature
   * @return {boolean}
   */
  get isPodEnabled() {
    return !!this.data.isPodEnabled;
  }

  /**
   * Check if the coach has enabled automatic track assignment.
   * Defaults to true (enabled) if not set.
   * @return {boolean}
   */
  get shouldEnableTrackAutoAssignment() {
    return this.data.shouldEnableTrackAutoAssignment ?? true;
  }

  /**
   * Check if the coach has disabled public tracks in auto assignment.
   * Defaults to false (public tracks allowed) if not set.
   * @return {boolean}
   */
  get shouldDisablePublicTrackAssignment() {
    return this.data.shouldDisablePublicTrackAssignment ?? false;
  }

  /**
   * Get the first check-in feedback template for the coach
   * @return {string}
   */
  get firstCheckInFeedbackTemplate() {
    return this.data.firstCheckInFeedbackTemplate || '';
  }

  // Method

  /**
   * Get coachConfig by coachId
   * @param {string} coachId - The coach id
   * @returns {object} - coachConfig doc
   */
  static async getCoachConfigByCoachId(coachId) {
    const coachConfigDoc = new CoachConfig(coachId);
    await coachConfigDoc.init();

    return coachConfigDoc;
  }

  /**
   * Get coach config by email
   * @param {string} email - The email address
   * @returns {array} - coachConfig docs
   */
  static async getCoachConfigByEmail(email) {
    const coachConfigCollection = new Collection(Collections.COACH_CONFIG, {
      createDocument: (src, opts) => new CoachConfig(src.id, opts),
      query: (ref) => ref
        .where('leadConfigs.emailAddress', '==', email),
    });
    await coachConfigCollection.fetch();
    return coachConfigCollection.docs;
  }

  /**
   * Update default order of activity feed based on the given attribute and value
   *
   * @param {string} property Which attribute going to be update
   * @param {string} order Order (ASC/DESC)
   * @return {Promise<void>}
   */
  setDefaultOrder = async (property, order) => {
    await this.set({
      activityFeedConfigs: {
        defaultOrder: {
          attributeName: property,
          order,
        },
      },
    }, { merge: true });
  };

  /**
   * Update activity feed archival time
   *
   * @param {number} daysToAutoArchive - Number of days coach activities will be regarded active
   *                                     before automatically removing them
   * @return {Promise<void>}
   */
  setDaysToAutoArchive = async (daysToAutoArchive) => {
    await this.set({
      activityFeedConfigs: {
        daysToAutoArchive,
      },
    }, { merge: true });
  }

  /**
   * Update workout assign reminder time
   *
   * @param {number} daysToRemindWorkoutAssign -  Number of days before a client needs new workouts
   *                                        when the coach will be notified.
   * @return {Promise<void>}
   */
  setDaysToRemindWorkoutAssign = async (
    currentDaysToRemindWorkoutAssign,
  ) => {
    const currentValue = Object.values(WorkoutReminderDays).includes(currentDaysToRemindWorkoutAssign)
      ? currentDaysToRemindWorkoutAssign
      : WorkoutReminderDays.ONE_DAY;

    await this.set({
      notificationConfigs: {
        daysToRemindWorkoutAssign: currentValue,
      },
    }, { merge: true });
  }

  /**
   * Clear all client journey weekly messaging for the coach
   * @return {Promise<void>}
   */
  clearClientJourneyWeeklyMessaging = async () => this.set({ clientJourneyWeeklyMessaging: [] }, { merge: true });

  /**
   * Update meal plan and workout assignment notification preference of future start clients.
   *
   * @param {boolean} isFutureStartNotificationEnabled - whether to enable notifications for
   *                                                    future start clients or not.
   * @return {Promise<void>}
   */
  async setIsFutureStartNotificationEnabled(isFutureStartNotificationEnabled) {
    return this.set({
      notificationConfigs: {
        isFutureStartNotificationEnabled,
      },
    }, { merge: true });
  }

  /**
   * Update coach's lead config settings
   *
   * @param {object} settings - object of attributes and their values to update
   * @param {string} [lastUpdatedByUserName] - user name of who updated the settings
   * @return {Promise<void>}
   */
  updateLeadConfig = async (settings, lastUpdatedByUserName) => {
    await this.set({
      leadConfigs: {
        ...settings,
      },
      ...(lastUpdatedByUserName && { lastUpdatedByUserName }),
    }, { merge: true });
  };

  /**
   * Update the coach's Automated Text Setter settings for a single touchpoint.
   * Percentage and template changes only affect newly triggered leads, since the agent
   * snapshots the rendered body when a message is queued.
   *
   * Written as a merge under the touchpoint's own key, so saving one touchpoint never
   * disturbs another's settings or the agent-written `runtime` state.
   *
   * @param {string} touchPoint - A SetterTouchPoint value
   * @param {object} settings - object of attributes and their values to update
   * @param {string} [lastUpdatedByUserName] - user name of who updated the settings
   * @return {Promise<void>}
   */
  updateAutomatedTextSetterConfig = async (touchPoint, settings, lastUpdatedByUserName) => {
    await this.set({
      automatedTextSetterConfigs: {
        [touchPoint]: {
          ...settings,
        },
      },
      ...(lastUpdatedByUserName && { lastUpdatedByUserName }),
    }, { merge: true });
  };

  /**
   * Update the coach's RCS outreach experiment settings. Only leads created after the change are
   * assigned with them; a lead keeps the channel it was given.
   *
   * @param {object} settings - RcsOutreachOptions attributes and their values
   * @param {string} [lastUpdatedByUserName] - user name of who updated the settings
   * @return {Promise<void>}
   */
  updateRcsOutreachConfig = async (settings, lastUpdatedByUserName) => {
    await this.set({
      rcsOutreachConfig: { ...settings },
      ...(lastUpdatedByUserName && { lastUpdatedByUserName }),
    }, { merge: true });
  };

  /**
   * Clear the verification circuit breaker, letting the agent send from this coach's account again.
   *
   * Only ever called once an admin has signed into the account and cleared the challenge by hand.
   * Clearing it while Instagram is still holding the account is what turns a challenge into a
   * lockout, so this is deliberate and never automatic.
   *
   * @param {string} [lastUpdatedByUserName] - user name of who cleared it
   * @return {Promise<void>}
   */
  resetAutomatedTextSetterCircuitBreaker = async (lastUpdatedByUserName) => {
    await this.set({
      automatedTextSetterConfigs: {
        runtime: {
          isCircuitBroken: false,
        },
      },
      ...(lastUpdatedByUserName && { lastUpdatedByUserName }),
    }, { merge: true });
  };

  /**
   * Update the archived public recipes list of the coach
   * @param {Array<string>} archivedRecipeIds - Array of recipe IDs
   * @return {Promise<void>}
   */
  updateArchivedRecipes = async (archivedRecipeIds) => {
    await this.set({
      mealPlanConfigs: { archivedPublicRecipes: archivedRecipeIds },
    }, { merge: true });
  }

  /**
   * Update webhook setup status as true for the coach
   * @return {Promise<void>}
   */
  markWebhookSetupCompletion = async (updatedByUserName) => {
    await this.set({
      isWebhookSetup: true,
      lastUpdatedByUserName: updatedByUserName,
    }, { merge: true });
  };

  /**
   * Sets the client journey weekly messaging for the coach
   * @param {Array} weeklyMessaging - Array of weekly messaging objects
   * @param {boolean} forcedWeeklyMessages - Whether the force send is enabled for the coach's client journey messaging
   * @returns {Promise<void>}
   */
  setClientJourneyWeeklyMessaging = async (weeklyMessaging, forcedWeeklyMessages) => this.set(
    { clientJourneyWeeklyMessaging: weeklyMessaging, forcedWeeklyMessages }, { merge: true },
  );

  /**
   * Enables the Pod feature for the coach.
   * Sets isPodEnabled to true in the coach config document.
   * @returns {Promise<void>}
   */
  enablePod = async () => this.set({ isPodEnabled: true }, { merge: true });
}

export default CoachConfig;
export {
  LeadConfigOptions,
  AutomatedTextSetterOptions,
  SetterTouchPoint,
  DEFAULT_SETTER_SEND_PERCENTAGE,
  MAX_SETTER_SEND_PERCENTAGE,
  RcsOutreachOptions,
  DEFAULT_RCS_PERCENTAGE,
  orderType,
  AutoArchiveDays,
  WorkoutReminderDays,
  ClientJourneyWeeks,
  MessageSendDays,
};
